// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React from "react";
import { RouteObject, NonIndexRouteObject } from "react-router-dom";
// APP imports
import { View } from "./view";
import { isIncludedRole } from "./utils/utils";
import { HR_ADMIN, FINANCE_ADMIN, EMPLOYEE_ROLE } from "@config/config";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BadgeIcon from '@mui/icons-material/Badge';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import InfoIcon from '@mui/icons-material/Info';

export interface RouteObjectWithRole extends NonIndexRouteObject {
  allowRoles: string[];
  icon:
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | undefined;
  text: string;
  children?: RouteObjectWithRole[];
  bottomNav?: boolean;
}

interface RouteDetail {
  path: string;
  allowRoles: string[];
  icon:
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | undefined;
  text: string;
  bottomNav?: boolean;
}

export const routes: RouteObjectWithRole[] = [
  {
    path: "/",
    text: "Bank Accounts",
    icon: React.createElement(AccountBalanceIcon),
    element: React.createElement(View.bankAccountView),
    allowRoles: [EMPLOYEE_ROLE], 
  },
  {
    path: "/change-requests",
    text: "Change Requests",
    icon: React.createElement(LibraryAddCheckIcon),
    element: React.createElement(View.changeRequestsView),
    allowRoles: [HR_ADMIN], 
  },
  {
    path: "/report",
    text: "Report",
    icon: React.createElement(AnalyticsIcon),
    element: React.createElement(View.reportView),
    allowRoles: [FINANCE_ADMIN, HR_ADMIN], 
  },
  {
    path: "/employee-operations",
    text: "Employee Operations",
    icon: React.createElement(BadgeIcon),
    element: React.createElement(View.employeeOperationsView),
    allowRoles: [FINANCE_ADMIN, HR_ADMIN], 
  },
  {
    path: "/admin",
    text: "Admin",
    icon: React.createElement(AdminPanelSettingsIcon),
    element: React.createElement(View.adminView),
    allowRoles: [FINANCE_ADMIN, HR_ADMIN],
  },
  {
    path: "/user-guide",
    text: "User Guide",
    icon: React.createElement(InfoIcon),
    element: React.createElement(View.userGuideView),
    allowRoles: [EMPLOYEE_ROLE], 
  }
];

export const getActiveRoutesV2 = (
  routes: RouteObjectWithRole[] | undefined,
  roles: string[]
): RouteObjectWithRole[] => {
  if (!routes) return [];
  var routesObj: RouteObjectWithRole[] = [];
  routes.forEach((routeObj) => {
    if (isIncludedRole(roles, routeObj.allowRoles)) {
      routesObj.push({
        ...routeObj,
        children: getActiveRoutesV2(routeObj.children, roles),
      });
    }
  });

  return routesObj;
};

export const getActiveRoutes = (roles: string[]): RouteObject[] => {
  var routesObj: RouteObject[] = [];
  routes.forEach((routeObj) => {
    if (isIncludedRole(roles, routeObj.allowRoles)) {
      routesObj.push({
        ...routeObj,
      });
    }
  });
  return routesObj;
};

export const getActiveRouteDetails = (roles: string[]): RouteDetail[] => {
  var routesObj: RouteDetail[] = [];
  routes.forEach((routeObj) => {
    if (isIncludedRole(roles, routeObj.allowRoles)) {
      routesObj.push({
        path: routeObj.path ? routeObj.path : "",
        ...routeObj,
      });
    }
  });
  return routesObj;
};
